import React, {useCallback, useRef, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import fr from 'video.js/dist/lang/fr.json';
import en from 'video.js/dist/lang/en.json';
import de from 'video.js/dist/lang/de.json';
import it from 'video.js/dist/lang/it.json';
// For DRM addition, see https://github.com/videojs/http-streaming#drm

import './VideoPlayer.scss';

const usePlayerState = (player, stateName, initialValue) => {
  const setValue = useCallback(
    newValue => {
      if (!player.current) return newValue;
      return player.current[stateName](newValue);
    },
    [stateName, player],
  );
  const value = player.current ? player.current[stateName]() : initialValue;

  return [value, setValue];
};

const usePlayerSetter = (player, handlerName, value, isValueValid = true) => {
  const [playerState, setPlayerState] = usePlayerState(player, handlerName, value);

  useEffect(() => {
    if (isValueValid && playerState !== value) {
      setPlayerState(value);
    }
  }, [playerState, value, isValueValid, setPlayerState]);
};

const VideoPlayer = React.memo(
  ({
    src,
    type = 'application/x-mpegURL',
    poster,
    width = 640,
    height = 264,
    controls = true,
    autoplay = false,
    paused = false,
    volume = 1,
    muted = false,
    fullscreen = false,
    fluid = true,
    startTimecode = 0,
    onTimeUpdate,
    onSeeking,
    onSeeked,
    onEnd,
    onVolumeChange,
    onTogglePlay,
  }) => {
    const {i18n} = useTranslation();

    const player = useRef(null);

    const handleFullscreen = wantFullScreen => {
      if (wantFullScreen && !player.current.isFullscreen()) {
        player.current.requestFullscreen();
      } else if (!wantFullScreen && player.current.isFullscreen()) {
        player.current.exitFullscreen();
      }
    };

    usePlayerSetter(player, 'volume', volume, volume !== null && volume <= 1 && volume >= 0);
    usePlayerSetter(player, 'muted', muted);

    useEffect(() => {
      if (!player.current) return;
      if (paused) {
        player.current.pause();
      } else {
        player.current.play();
      }
    }, [paused]);

    useEffect(() => {
      if (!player.current) return;
      try {
        player.current.currentTime(startTimecode);
      } catch (error) {
        console.log('VideoPlayer error', error);
      }
    }, [startTimecode]);

    useEffect(() => {
      if (!player.current) return;
      handleFullscreen(fullscreen);
    }, [fullscreen]);

    const setRef = useCallback(
      node => {
        if (!node || player.current) return;

        const videoJsOptions = {
          autoplay,
          controls,
          width,
          height,
          poster,
          volume,
          muted,
          fluid,
          language: i18n.language,
          // playbackRates: [1],
          responsive: true,
          userActions: {
            doubleClick: true, // onDoubleClick
            // hotkeys: true, or {} or handler
          },
          languages: {
            fr,
            en,
            de,
            it,
          },
          // html5: {
          //   vhs: {
          //     // withCredentials: true,
          //     overrideNative: !videojs.browser.IS_SAFARI,
          //   },
          // },
          sources: [
            {
              src,
              type,
              withCredentials: true,
            },
          ],
        };
        player.current = videojs(node, videoJsOptions, () => {
          handleFullscreen(fullscreen);
          // player.current.tech().vhs.xhr.beforeRequest = function modifyRequest(options) {
          //   return {
          //     ...options,
          //   };
          // };

          player.current.on('timeupdate', () => {
            if (!player.current || !onTimeUpdate) return;
            try {
              onTimeUpdate(player.current.currentTime());
            } catch (error) {
              console.log('VideoPlayer error', error);
            }
          });

          player.current.on('seeking', () => {
            if (!player.current || !onSeeking) return;
            try {
              onSeeking(player.current.currentTime());
            } catch (error) {
              console.log('VideoPlayer error', error);
            }
          });

          player.current.on('seeked', () => {
            if (!player.current || !onSeeked) return;
            try {
              onSeeked(player.current.currentTime());
            } catch (error) {
              console.log('VideoPlayer error', error);
            }
          });

          player.current.on('ended', () => {
            if (!player.current || !onEnd) return;
            onEnd();
          });

          player.current.on('volumechange', () => {
            if (!player.current || !onVolumeChange) return;
            try {
              onVolumeChange(player.current.volume());
            } catch (error) {
              console.log('VideoPlayer error', error);
            }
          });

          player.current.on('pause', () => {
            if (!player.current || !onTogglePlay) return;
            onTogglePlay(false);
          });

          player.current.on('play', () => {
            if (!player.current || !onTogglePlay) return;
            onTogglePlay(true);
          });
        });
        player.current.removeChild('TextTrackSettings');
        // player.current.removeChild('ControlBar');
        const {controlBar} = player.current;
        // const controlBar = player.current.addChild('ControlBar');
        controlBar.removeChild('VolumePanel');
        controlBar.removeChild('FullscreenToggle');
        controlBar.removeChild('SubtitlesButton');
        // controlBar.removeChild('CaptionsButton');
        controlBar.removeChild('AudioTrackButton');
        controlBar.removeChild('PictureInPictureToggle');
        controlBar.removeChild('ProgressControl');

        // controlBar.addChild('ProgressControl');

        // controlBar.addChild('PlayToggle');
        // controlBar.addChild('CurrentTimeDisplay');
        // controlBar.addChild('TimeDivider');
        controlBar.addChild('DurationDisplay');

        controlBar.addChild('VolumePanel', {inline: false, vertical: true});
        // controlBar.addChild('SubtitlesButton');
        // controlBar.addChild('CaptionsButton');
        controlBar.addChild('AudioTrackButton');

        controlBar.addChild('FullscreenToggle');

        // progressBar.removeChild('SeekBar');

        // controlBar.addChild('LiveDisplay');
        // controlBar.addChild('SeekToLive');

        // controlBar.addChild('RemainingTimeDisplay');
        // controlBar.addChild('CustomControlSpacer');
        // controlBar.addChild('PlaybackRateMenuButton');
        // controlBar.addChild('ChaptersButton');
        // controlBar.addChild('DescriptionsButton');
        // controlBar.addChild('SubsCapsButton');

        // controlBar.addChild('PictureInPictureToggle');
      },
      [
        src,
        type,
        autoplay,
        width,
        height,
        poster,
        volume,
        muted,
        fluid,
        fullscreen,
        i18n.language,
        onEnd,
        onSeeked,
        onSeeking,
        onTimeUpdate,
        controls,
        onVolumeChange,
        onTogglePlay,
      ],
    );

    useEffect(() => {
      return () => {
        if (player.current) {
          player.current.dispose();
        }
      };
    }, []);

    return (
      <div data-vjs-player>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video ref={setRef} className="video-js vjs-customization" />
      </div>
    );
  },
);

VideoPlayer.propTypes = {
  volume: function(props, propName) {
    if (props[propName] && (typeof props[propName] !== 'number' || props[propName] < 0 || props[propName] > 1)) {
      return new Error('Invalid volume prop');
    }
  },
};

export default VideoPlayer;
